// GameAnalyzer.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GameAnalyzerChart from '../../charts/GameAnalyzerChart';
import ResultsOverview from './ResultsOverview';
import Dropdown from '../../Assets/Dropdown';
import { ArrowLeftIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import GameAnalyzerError from './GameAnalzerError';

// Import sport-specific adapters
import { getSportAdapter } from './sportAdapers';

// Import RTK Query hooks
import { useGetNflGameScoresQuery } from '../../../services/api/nflApi';
import { useGetNbaGameScoresQuery } from '../../../services/api/nbaApi';
import { useGetMlbGameScoresQuery } from '../../../services/api/mlbApi';

const GameAnalyzer = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const game = location.state?.game || null;
    const sport = location.state?.sport || null;
    
    // Get the sport-specific adapter
    const sportAdapter = getSportAdapter(sport);
    
    // Common state variables
    const [chartType, setChartType] = useState(sportAdapter?.defaultChartType || null);
    const [periodSelection, setPeriodSelection] = useState(sportAdapter?.defaultPeriodSelection || null);
    const [teamSelection, setTeamSelection] = useState();
    const [numGames, setNumGames] = useState(5);
    const [periodDropdownOptions, setPeriodDropdownOptions] = useState(null);
    const [teamDropdownOptions, setTeamDropdownOptions] = useState(null);
    const [resultSets, setResultSets] = useState(null);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    // Common chart colors
    const chartColors = {
        homeColor: "rgba(66, 135, 245, 0.75)",
        homeBorderColor: "rgba(66, 135, 245, 1)",
        awayColor: 'rgba(255, 45, 85, .75)',
        awayBorderColor: 'rgba(255, 45, 85, 1)',
        winColor: 'rgba(52, 199, 89, .75)',
        winBorder: 'rgba(52, 199, 89, 1)'
    };

    // Prepare query parameters based on sport
    const queryParams = useMemo(() => {
        if (!game || !sport) return null;
        
        if (sport === 'mlb') {
            return {
                homeTeam: game.home?.team,
                awayTeam: game.away?.team
            };
        } else {
            return {
                homeTeamId: game.home?.team_id,
                awayTeamId: game.away?.team_id
            };
        }
    }, [game, sport]);

    // Call RTK Query hooks (all hooks must be called unconditionally)
    const nflQuery = useGetNflGameScoresQuery(
        queryParams && sport === 'nfl' ? queryParams : { homeTeamId: '', awayTeamId: '' },
        { skip: !queryParams || sport !== 'nfl' }
    );
    
    const nbaQuery = useGetNbaGameScoresQuery(
        queryParams && sport === 'nba' ? queryParams : { homeTeamId: '', awayTeamId: '' },
        { skip: !queryParams || sport !== 'nba' }
    );
    
    const mlbQuery = useGetMlbGameScoresQuery(
        queryParams && sport === 'mlb' ? queryParams : { homeTeam: '', awayTeam: '' },
        { skip: !queryParams || sport !== 'mlb' }
    );

    // Select the appropriate query result based on sport
    const { data: scoresData, error: apiError, isFetching: isApiFetching, isError: isApiError } = useMemo(() => {
        if (sport === 'nfl') return nflQuery;
        if (sport === 'nba') return nbaQuery;
        if (sport === 'mlb') return mlbQuery;
        return { data: null, error: null, isLoading: false, isError: false };
    }, [sport, nflQuery, nbaQuery, mlbQuery]);

    // Handle navigation to sign-in on 401 errors
    useEffect(() => {
        if (apiError && 'status' in apiError && apiError.status === 401) {
            navigate('/signin');
        }
    }, [apiError, navigate]);

    // Initialize on component mount - redirect if no game or sport
    useEffect(() => {
        if (game === null || sport === null) {
            navigate('/upcoming-games');
        }
    }, [game, sport, navigate]);

    // Process scores data to game data structure
    const gamesData = useMemo(() => {
        if(!scoresData || !game || isApiFetching) return null;
        return sportAdapter.processScoresData(scoresData).gamesData;
    }, [isApiFetching, scoresData, game, sportAdapter]);

    const bettingLineResults = useMemo(() => {
        if(!chartType || !periodSelection || !gamesData || !game) return null;
        return sportAdapter.calculateBettingResults(gamesData, game, chartType, periodSelection);
    }, [chartType, periodSelection, gamesData, game, sportAdapter]);

    // Helper functions for statistics
    const getAverages = useCallback((data) => {
        const calculateAverage = arr => (arr.reduce((sum, value) => sum + value, 0) / arr.length).toFixed(1);
        return [5, 10, 15, 20, 30].map(n => calculateAverage(data.slice(-n)));
    }, []);

    // Win/Loss Sets for Overview Section
    const getWLSets = useCallback((results) => {
        return [5, 10, 15, 20, 30].map(n => {
            let res = { w: 0, l: 0, p: 0 };
            const slice = results.slice(-n);
            
            for (let i = 0; i < slice.length; i++) {
                if (slice[i] === 1) res.w++;
                else if (slice[i] === 0) res.l++;
                else if (slice[i] === 2) res.p++;
            }
            
            const record = `${res.w}-${res.l}-${res.p}`;
            const winPer = res.w + res.l > 0 ? (res.w/(res.w + res.l)*100).toFixed(1) : "N/A";
            
            return { record, winPer };
        });
    }, []);

    // Generate results for various chart types
    const getResults = useCallback(() => {
        if (!gamesData) return;
        
        return sportAdapter.generateResults({
            chartType,
            periodSelection,
            teamSelection,
            gamesData,
            bettingLineResults,
            getWLSets,
            getAverages
        });
    }, [periodSelection, teamSelection, gamesData, bettingLineResults, sportAdapter, getWLSets, getAverages]);

    // Update results when selections change
    useEffect(() => {        
        const shouldUpdateResults = sportAdapter?.shouldUpdateResults({
            chartType,
            periodSelection,
            teamSelection,
            gamesData,
            bettingLineResults,
            resultSets,
            periodDropdownOptions,
            teamDropdownOptions,
        }) || false;
        
        if (shouldUpdateResults) {
            const newResultSets = getResults();
            setResultSets(newResultSets);
        }
    }, [chartType, periodSelection, teamSelection, gamesData, bettingLineResults, 
        periodDropdownOptions, teamDropdownOptions, sportAdapter, getResults]);

    // Get team colors for chart
    const getTeamColors = (team) => {
        const isHomeTeam = sport === 'mlb' 
            ? team === game?.home?.team
            : team === game?.home?.team_id;
            
        return isHomeTeam
            ? { color: chartColors.homeColor, border: chartColors.homeBorderColor }
            : { color: chartColors.awayColor, border: chartColors.awayBorderColor };
    };

    // Update chart options when chart type changes
    useEffect(() => {
        const periodOptions = sportAdapter?.getPeriodDropdownOptions(chartType, game) || null;
        const teamOptions = sportAdapter?.getTeamDropdownOptions(chartType, game) || null;
        setPeriodDropdownOptions(periodOptions?.options || null);
        setTeamDropdownOptions(teamOptions?.options || null);

        if(!periodOptions || !periodOptions.options){
            setPeriodSelection(null);
        }else if (periodOptions.defaultSelection && 
            (periodOptions.forceDefault || !periodSelection || !periodOptions.options?.some(opt => opt.key === periodSelection))) {
            setPeriodSelection(periodOptions.defaultSelection);
        }
        if(!teamOptions || !teamOptions.options){
            setTeamSelection(null);
        }else if (teamOptions.defaultSelection && 
            (teamOptions.forceDefault || !teamSelection || !teamOptions.options?.some(opt => opt.key === teamSelection))) {
            setTeamSelection(teamOptions.defaultSelection);
        }
    }, [chartType, game, sportAdapter]);

    // Memoize chart parameters
    const chartParams = useMemo(() => {
        if (!gamesData || isApiFetching) return null;

        const formatParams = {
            chartType,
            periodSelection,
            teamSelection,
            numGames,
            gamesData,
            bettingLineResults,
            getTeamColors,
            chartColors,
            game,
        };
        
        return sportAdapter.formatChartData(formatParams);
    }, [isApiFetching, chartType, periodSelection, teamSelection, numGames, gamesData, bettingLineResults, 
        chartColors, sportAdapter, game]);

    // Change number of games to display
    const changeNumGames = (num) => {
        setNumGames(num);
        
        // Update UI classes for selected games range
        const oldElements = document.getElementsByClassName('selected');
        if (oldElements.length > 0) {
            Array.from(oldElements).forEach(element => {
                element.classList.remove('selected');
            });
        }
        
        const newElements = document.getElementsByClassName(`last${num}-games`);
        if (newElements.length > 0) {
            Array.from(newElements).forEach(element => {
                element.classList.add('selected');
            });
        }
    };

    return (
        <div className="bg-gray-50 dark:bg-brand-dark-900 h-full">
            {/* Mobile filter dialog */}
            <div className={`fixed inset-0 z-40 lg:hidden ${mobileFiltersOpen ? 'block' : 'hidden'}`}>
                {/* Mobile filter content */}
                <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setMobileFiltersOpen(false)}></div>
                <div className="fixed inset-y-0 left-0 z-40 w-full max-w-xs overflow-y-auto bg-white dark:bg-gray-900 p-6 shadow-xl transform transition-transform duration-300">
                    {/* Filter controls */}
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Analysis Controls</h2>
                        <button
                            type="button"
                            className="-mr-2 flex h-10 w-10 items-center justify-center rounded-md p-2 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-white"
                            onClick={() => setMobileFiltersOpen(false)}
                        >
                            <span className="sr-only">Close menu</span>
                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile filter options */}
                    <div className="space-y-6">
                        {/* Chart type dropdown */}
                        <div className="w-full">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Chart Type</h3>
                            {sportAdapter && (
                                <Dropdown
                                    title="Select Chart Type"
                                    items={sportAdapter.getChartTypeOptions()}
                                    activeItem={chartType}
                                    onSelect={setChartType}
                                    widthClasses="w-full"
                                />
                            )}
                        </div>

                        {/* Period dropdown if available */}
                        {periodDropdownOptions && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Period</h3>
                                <Dropdown
                                    title="Select Period"
                                    items={periodDropdownOptions.map(opt => ({
                                        value: opt.key,
                                        label: opt.value
                                    }))}
                                    activeItem={periodSelection}
                                    onSelect={setPeriodSelection}
                                    className="mt-2 w-full"
                                />
                            </div>
                        )}
                        {/* Team dropdown if available */}
                        {teamDropdownOptions && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Team</h3>
                                <Dropdown
                                    title="Select Team"
                                    items={teamDropdownOptions.map(opt => ({
                                        value: opt.key,
                                        label: opt.value
                                    }))}
                                    activeItem={teamSelection}
                                    onSelect={setTeamSelection}
                                    className="mt-2 w-full"
                                />
                            </div>
                        )}

                        {/* Games range buttons */}
                        <div>
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Games Range</h3>
                            <div className="mt-2 flex flex-col gap-2">
                                {["L5", "L10", "L15", "L20", "L30"].map((label) => {
                                    const num = parseInt(label.slice(1));
                                    const isSelected = num === numGames;
                                    return (
                                        <button
                                            key={label}
                                            onClick={() => changeNumGames(num)}
                                            className={`flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                isSelected 
                                                    ? 'bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white' 
                                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                            }`}
                                        >
                                            {label} Games
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row h-full">
                {/* Left sidebar (desktop) */}
                <aside className="hidden lg:block lg:sticky lg:top-0 lg:w-72 lg:border-r lg:border-gray-200 dark:lg:border-gray-700 lg:overflow-y-auto bg-white dark:bg-brand-dark-900 p-4 shrink-0">
                    <div className="sticky top-0 space-y-6">
                        {/* Header with back button */}
                        <div className="pb-5 border-b border-gray-200 dark:border-gray-700">
                            <button 
                                onClick={() => navigate(`/upcoming-games`, {state:{game:game, sport:sport}})}
                                className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white mb-4"
                            >
                                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                Back to Game
                            </button>
                            <h1 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">
                                {sport === 'mlb' ? game?.away?.team : game?.away?.team_id} @ {sport === 'mlb' ? game?.home?.team : game?.home?.team_id}
                            </h1>
                        </div>

                        {/* Desktop filter controls */}
                        {/* Chart Type Selection */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Chart Type</span>
                                </div>
                            </div>
                            <div className="mt-4">
                                {sportAdapter && (
                                    <Dropdown
                                        title="Select Chart Type"
                                        items={sportAdapter.getChartTypeOptions()}
                                        activeItem={chartType}
                                        onSelect={setChartType}
                                        widthClasses="w-full"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Period dropdown if available */}
                        {periodDropdownOptions && (
                            <div>
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Period</h3>
                                <Dropdown
                                    title="Select Period"
                                    items={periodDropdownOptions.map(opt => ({
                                        value: opt.key,
                                        label: opt.value
                                    }))}
                                    activeItem={periodSelection}
                                    onSelect={setPeriodSelection}
                                    className="mt-2 w-full"
                                />
                            </div>
                        )}
                        {/* Team dropdown if available */}
                        {teamDropdownOptions && (
                            <div>
                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                        <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                    </div>
                                    <div className="relative flex justify-start">
                                        <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Team</span>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <Dropdown
                                        title="Select Team"
                                        items={teamDropdownOptions.map(opt => ({
                                            value: opt.key,
                                            label: opt.value
                                        }))}
                                        activeItem={teamSelection}
                                        onSelect={setTeamSelection}
                                        widthClasses="w-full"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Games Range Selection */}
                        <div>
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                                    <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <span className="bg-white dark:bg-brand-dark-900 pr-3 text-sm font-medium text-gray-900 dark:text-white">Games Range</span>
                                </div>
                            </div>
                            <div className="space-y-2 mt-4">
                                {["L5", "L10", "L15", "L20", "L30"].map((label) => {
                                    const num = parseInt(label.slice(1));
                                    const isSelected = num === numGames;
                                    return (
                                        <button
                                            key={label}
                                            onClick={() => changeNumGames(num)}
                                            className={`flex w-full items-center justify-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                                isSelected 
                                                    ? 'bg-brand-red-50 dark:bg-brand-red-900 text-brand-red-700 dark:text-white' 
                                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                                            }`}
                                        >
                                            {label} Games
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Main content */}
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-brand-dark-900">
                    {/* Mobile header */}
                    <div className="lg:hidden sticky top-0 z-10 bg-white dark:bg-brand-dark-900 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
                        <div className="flex flex-col space-y-4">
                            <div className="flex items-center justify-between">
                                <button 
                                    onClick={() => navigate(`/upcoming-games`, {state:{game:game, sport:sport}})}
                                    className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                                >
                                    <ArrowLeftIcon className="h-5 w-5 mr-2" />
                                    Back to Game
                                </button>
                            </div>
                            <button
                                type="button"
                                className="flex items-center justify-center gap-2 w-full p-2 rounded-md bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700"
                                onClick={() => setMobileFiltersOpen(true)}
                            >
                                <AdjustmentsHorizontalIcon className="h-5 w-5" />
                                <span className="text-sm font-medium">Analysis Controls</span>
                            </button>
                        </div>
                    </div>

                    {/* Chart and Results Content */}
                    <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-7xl mx-auto">
                        {gamesData && !isApiFetching ? (
                            <div className="space-y-6">
                                {/* Results Overview Section */}
                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-800/30 p-4">
                                    <ResultsOverview 
                                        resultSets={resultSets} 
                                        strNumGames={`L${numGames}`} 
                                        changeNumGames={changeNumGames} 
                                        apiError={!!apiError}
                                    />
                                </div>
                                {/* Chart Section */}
                                {chartParams && (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-800/30 p-4">
                                        <GameAnalyzerChart 
                                            chartType={chartType} 
                                            data={chartParams.chartData} 
                                            options={chartParams.chartOptions} 
                                            chartLabel={chartParams.chartLabel} 
                                            horizontalLine={chartParams.horizontalLine}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center min-h-64 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-800/30">
                                {apiError && !isApiFetching ? (
                                    <GameAnalyzerError error={apiError} />
                                ) : isApiFetching ? (
                                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-red-500 border-t-transparent"></div>
                                ) : (
                                    <div className="text-gray-500 dark:text-gray-400">Data Unavailable</div>
                                )}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default GameAnalyzer;